/**
 * Basemaps — spec §F5.1 ("Basemaps: light `basemaps.cartocdn.com/light_all`, streets OSM,
 * satellite Esri World_Imagery, terrain OpenTopoMap").
 *
 * **The catalogue is now served, not compiled in.** `/meta/config.basemaps` is the source of
 * truth (`apps/meta/basemaps.py`): the server decides which CARTO URL form to hand out,
 * because only the server knows what CARTO configuration the deployment holds, and a key
 * belongs in an environment variable rather than in a bundle every reader downloads. Where
 * CARTO tiles are configured the CARTO rows carry the keyed URLs, a `dark` row appears and
 * `light` is the served cold-boot default (`app/BasemapDefault.tsx`); where they are not, the
 * served list is exactly the four rows below and `streets` stays the default.
 *
 * The list here is the **fallback**, for the one case that has to keep working: a client
 * holding a `/meta/config` payload from before this feature (the response is memoised for
 * five minutes server-side and cached in the browser for the same), or a boot where the
 * config query has not resolved yet. It is byte-identical to what the keyless server
 * serves, so falling back is invisible.
 *
 * URLs, attributions, display names and thumbnail swatches are the mockup's `BASEMAPS`
 * object (plan Global Constraints: the mockup wins on look). Keys are §F1
 * `AppState.basemap`, which the permalink's `bm=` carries, so they are codes and are
 * stable in both directions.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchConfig, queryKeys, type MetaConfig } from '../../lib/api';
import type { Basemap } from '../../lib/state';

/** A basemap as the panel and the map need it — the served row, in the app's own names. */
export interface BasemapEntry {
  key: Basemap;
  /** Label shown in the §F5.10 basemap panel. Server-translated when it is served. */
  name: string;
  url: string;
  attr: string;
  /** Swatch colour for the basemap panel's thumb (mockup `.bm-thumb`). */
  thumb: string;
  /** Deepest zoom the provider publishes. Well above §F5.1's `MAX_ZOOM` (12) in every case. */
  maxZoom: number;
}

/** The `BasemapEntry` minus its key — the shape the fallback record is written in. */
export type BasemapDef = Omit<BasemapEntry, 'key'>;

/**
 * The swatches, which stay client-side.
 *
 * A thumb is a look-and-feel constant the mockup owns, not configuration: the server has no
 * opinion about it and `/meta/config.basemaps` does not carry one. Keyed by basemap key so a
 * row the server adds (`dark`) gets its swatch here; anything unrecognised gets the neutral.
 */
export const BASEMAP_THUMBS: Record<Basemap, string> = {
  light: '#E8EAE6',
  dark: '#2B2F33',
  streets: '#DCE8CF',
  satellite: '#3D4A33',
  terrain: '#D8CBA8',
};

/** Swatch for a key the server sent and this build has never heard of. */
const DEFAULT_THUMB = '#C9CCC6';

const thumbFor = (key: string): string =>
  (BASEMAP_THUMBS as Record<string, string | undefined>)[key] ?? DEFAULT_THUMB;

export const BASEMAPS: Record<'light' | 'streets' | 'satellite' | 'terrain', BasemapDef> = {
  light: {
    name: 'Light (Carto)',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attr: '© OpenStreetMap © CARTO',
    thumb: BASEMAP_THUMBS.light,
    maxZoom: 20,
  },
  streets: {
    name: 'Streets (OSM)',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attr: '© OpenStreetMap contributors',
    thumb: BASEMAP_THUMBS.streets,
    maxZoom: 19,
  },
  satellite: {
    name: 'Satellite (Esri)',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attr: 'Esri, Maxar, Earthstar Geographics',
    thumb: BASEMAP_THUMBS.satellite,
    maxZoom: 19,
  },
  terrain: {
    name: 'Terrain (OpenTopo)',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attr: '© OpenTopoMap (CC-BY-SA)',
    thumb: BASEMAP_THUMBS.terrain,
    maxZoom: 17,
  },
};

/** Ordered keys of the built-in list — what the keyless server serves, in its order. */
export const BASEMAP_KEYS = ['light', 'streets', 'satellite', 'terrain'] as const;

/**
 * Every key §F1 `basemap` may hold, including the ones only a configured server serves.
 *
 * The permalink validates `bm=` against *this*, not against `BASEMAP_KEYS`: a link written
 * on a deployment with `CARTO_API_KEY` set says `bm=dark`, and it has to survive the round
 * trip on the same deployment. `resolveBasemap` handles the other direction — a `dark` link
 * opened where dark is not served falls back to the default rather than to a blank map.
 */
export const ALL_BASEMAP_KEYS = ['light', 'dark', 'streets', 'satellite', 'terrain'] as const;

/** §F1's cold-boot basemap, and the resolution target for a key the server does not serve. */
export const DEFAULT_BASEMAP: Basemap = 'streets';

/** The §F1 default as an entry — the last resort of `resolveBasemap`, and never undefined. */
const STREETS: BasemapEntry = { key: 'streets', ...BASEMAPS.streets };

/** The built-in list as entries — what `basemapsFromConfig` falls back to. */
export const FALLBACK_BASEMAPS: BasemapEntry[] = BASEMAP_KEYS.map((key) => ({
  key,
  ...BASEMAPS[key],
}));

/**
 * `/meta/config.basemaps` as entries, or the built-in list when the block is absent.
 *
 * Absent means one of two things and is treated the same way in both: the config query has
 * not resolved yet, or the payload predates this feature (a five-minute server cache and the
 * browser's own `max-age=300` both outlive a deploy). An *empty* served list is also a
 * fallback — a server that serves no basemaps at all is a bug, not an instruction to render
 * a map with no tiles.
 */
export function basemapsFromConfig(config: MetaConfig | undefined): BasemapEntry[] {
  const served = config?.basemaps?.list;
  if (!served || served.length === 0) return FALLBACK_BASEMAPS;
  return served.map((row) => ({
    key: row.key as Basemap,
    name: row.label,
    url: row.url,
    attr: row.attribution,
    thumb: thumbFor(row.key),
    maxZoom: row.maxZoom,
  }));
}

/**
 * The served basemap catalogue. Shared by the panel, the map and the §F4 mini-map.
 *
 * `useMemo` is load-bearing, not tidiness: `MapView` has the list in the dependency array of
 * the effect that builds the tile layer, and a fresh array on every render would tear that
 * layer down and rebuild it on every render — every tile refetched, on every store change.
 * React Query's `data` is reference-stable between renders, so one memo on it is enough.
 */
export function useBasemaps(): BasemapEntry[] {
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  return useMemo(() => basemapsFromConfig(config), [config]);
}

/**
 * The entry for `key`, or the default, or the first row.
 *
 * Two keys can name a basemap the current deployment does not serve: `bm=dark` from a link
 * written against a configured server, and a stale §F1 store from a session that had one.
 * Neither should leave the reader with no tiles at all, so both land on `streets` — the
 * §F1 default, which every list contains.
 */
export function resolveBasemap(list: BasemapEntry[], key: Basemap): BasemapEntry {
  return (
    list.find((b) => b.key === key) ??
    list.find((b) => b.key === DEFAULT_BASEMAP) ??
    list[0] ??
    STREETS
  );
}
